// Fusion d'une liste de TransactionV2 locale (saisie de la fiche, pas encore
// POSTée) avec la liste servie par `api/transactionv2`.
//
// Ce refetch existe pour récupérer un changement SERVEUR — quittance générée,
// facture uploadée, document supprimé — jamais pour arbitrer la saisie en
// cours. Le remplacement pur et simple faisait donc disparaître sans un mot
// les lignes non sauvegardées (Fourrière d'une fiche dépannage neuve,
// Restitution, ligne en cours d'édition).
//
// Règles :
//  - id non entier (null, Math.random()) = ligne jamais sauvegardée → intacte ;
//  - ligne verrouillée (`dep_mission` posé : encaissement terrain) → le
//    serveur fait foi, l'UI ne la laisse de toute façon pas éditer ;
//  - ligne fiche déjà sauvegardée → on ne reprend du serveur que ses
//    documents (`quittance` / `facture`) ; revue 27.10 (🔴) : si la facture
//    a changé côté serveur (dépôt OU retrait), sa date aussi — le dépôt
//    d'une facture date la ligne « Facture », son retrait l'efface ; sinon
//    la date locale (saisie en cours) est gardée ;
//  - ligne servie inconnue localement → ajoutée en fin de liste.
export function mergeTransactionsV2(locales, serveur) {
	const distantes = Array.isArray(serveur) ? serveur : []
	const parId = new Map(
		distantes.filter((t) => Number.isInteger(t.id)).map((t) => [t.id, t]),
	)
	const fusionnees = (locales || []).map((locale) => {
		if (!Number.isInteger(locale.id)) return locale
		const distante = parId.get(locale.id)
		if (!distante) return locale
		parId.delete(locale.id)
		if (locale.dep_mission || distante.dep_mission) return distante
		const fusion = {
			...locale,
			quittance: distante.quittance,
			facture: distante.facture,
		}
		if (cheminDocument(locale.facture) !== cheminDocument(distante.facture))
			fusion.date = distante.date || ""
		return fusion
	})
	return [...fusionnees, ...distantes.filter((t) => parId.has(t.id))]
}

// Chemin du fichier d'un document servi ({url} signée : /media/_s/<exp>/<sig>/
// <chemin>, signature renouvelée à chaque lecture) ; "" sans document. Un PDF
// choisi localement (blob, pas encore envoyé) n'a pas de chemin serveur.
const cheminDocument = (doc) => {
	const url = (doc && doc.url) || ""
	if (!url || !doc.uploaded) return url
	return url.replace(/^.*?\/_s\/\d+\/[^/]+\//, "")
}

export function estPerte(transaction) {
	return transaction.payeParSelect === 2
}

export function appliquerPerte(transaction) {
	return { ...transaction, moyenPaie: 0, caissier: 0, date: "" }
}

export default mergeTransactionsV2

// ─── Règle de modification, commune aux 3 fiches (Jordan 19.10) ─────────────
// Miroir de utilitaires/transactions_regle + utilitaires/datation (serveur).
// Quatre niveaux : boss (tout, fiche close comprise ; la fiche confirme en
// disant la conséquence), admin (Jordan 30.10 : le boss moins la fiche
// close), responsable (Jordan 30.10 : à tout état, tout sur une ligne SANS
// date, rien sur une ligne datée), employé. Les règles
// portent sur les valeurs ENREGISTRÉES (`enregistre`, servi par
// api/transactionv2 ; la saisie locale réécrit les champs de la ligne).

export const BOSS = "boss"
export const ADMIN = "admin"
export const RESPONSABLE = "responsable"
export const EMPLOYE = "employe"

// Miroir de RESPONSABLES (utilitaires/transactions_regle) : module de la
// fiche → groupes responsables (Jordan 27.10). Jordan 30.10 : plus de limite
// d'état, c'est la date de la ligne qui fige.
const RESPONSABLES = {
	atelier: ["responsable_atelier", "admin_atelier"],
	location: ["responsable_atelier", "admin_atelier"],
	depannage: ["responsable_depannage", "admin_depannage"],
}

// `module` et `etat` : la fiche et son état ENREGISTRÉ — sans fiche
// enregistrée, jamais « responsable » (la fiche close est gérée à part).
export const niveauTransactions = ({
	isBoss,
	isAdmin,
	groupes = [],
	module,
	etat,
}) => {
	if (isBoss) return BOSS
	if (isAdmin) return ADMIN
	const r = RESPONSABLES[module]
	if (r && Number.isInteger(etat) && r.some((g) => groupes.includes(g)))
		return RESPONSABLE
	return EMPLOYE
}

const PAYE_PAR_PERTE = 2
const enBase = (tx) => tx.enregistre || {}
const enregistree = (tx) => Number.isInteger(tx.id)

// Ligne de la fiche + ce que le serveur sait d'elle (valeurs enregistrées,
// WinBIZ, rapprochement), d'après la ligne servie de même id. À défaut
// d'`enregistre`, la ligne servie EST la valeur enregistrée.
export const avecServeur = (tx, serveur) => {
	const base = (serveur || []).find((b) => b.id === tx.id)
	if (!enregistree(tx) || !base) return tx
	return {
		...tx,
		enregistre: base.enregistre || {
			date: base.date || null,
			total: base.total,
			moyenPaie: base.moyenPaie || 0,
			payeParSelect: base.payeParSelect,
			caissier: base.caissier || 0,
		},
		winbiz: base.winbiz ?? !!(base.extra_data || {}).winbiz,
		rapprochee: !!base.rapprochee,
		quittance: tx.quittance,
		facture: tx.facture,
	}
}

// Date : dater une ligne non datée = droit « encaissements » (jamais
// Facture ni perte : une Facture se date au dépôt de sa facture, Jordan
// 27.10) ; corriger / effacer = boss / admin (Jordan 30.10 : plus le
// responsable), WinBIZ et rapprochée comprises.
export const regleDatation = (
	tx,
	{ droitEncaissements, niveau, mdpFactureId },
) => {
	const non = (raison) => ({ modifiable: false, raison })
	const bossAdmin = niveau === BOSS || niveau === ADMIN
	if (tx.payeParSelect === PAYE_PAR_PERTE) return non("Perte : pas de date")
	if (tx.winbiz && !bossAdmin)
		return non("Envoyée à WinBIZ : modifiable par le boss ou l'admin")
	if (!enBase(tx).date) {
		if (!droitEncaissements)
			return non("Dater : droit « Encaissements » requis")
		if (mdpFactureId && tx.moyenPaie === mdpFactureId)
			return non(
				"Moyen « Facture » : la date se pose au dépôt de sa facture",
			)
		return { modifiable: true, raison: null }
	}
	if (niveau === RESPONSABLE)
		return non("Encaissée : modifiable par le boss ou l'admin")
	if (!bossAdmin)
		return non("Date posée : correction réservée au boss ou à l'admin")
	return { modifiable: true, raison: null }
}

// → {moyen, payeur, montant, caissier, details, raison} (booléens) +
// `pourquoi` (infobulle des champs figés).
export const champsModifiables = (tx, { niveau }) => {
	const bossAdmin = niveau === BOSS || niveau === ADMIN
	const tous = (v) => ({
		moyen: v,
		payeur: v,
		montant: v,
		caissier: v,
		details: v,
		raison: v,
	})
	// Jordan 30.10 : ligne datée figée pour le responsable.
	if (enregistree(tx) && niveau === RESPONSABLE && enBase(tx).date)
		return {
			...tous(false),
			pourquoi: "Encaissée : modifiable par le boss ou l'admin",
		}
	if (tx.winbiz && !bossAdmin)
		return {
			...tous(false),
			pourquoi: "Envoyée à WinBIZ : modifiable par le boss ou l'admin",
		}
	if (tx.dep_mission && niveau === EMPLOYE)
		return {
			...tous(false),
			pourquoi:
				"Encaissement terrain : modifiable par le boss ou l'admin",
		}
	const c = {
		...tous(true),
		// Caissier figé une fois la ligne enregistrée, détails réservés au
		// boss / admin.
		caissier: !enregistree(tx) || niveau !== EMPLOYE,
		// R7 (revue interne 19.10) : comme le serveur — une ligne neuve
		// reçoit ses détails, une ligne enregistrée : boss / admin.
		details: !enregistree(tx) || niveau !== EMPLOYE,
		pourquoi: null,
	}
	// Re-revue 19.10 (🟠 R3) : un document retiré fige aussi (même prédicat
	// que le serveur et que causeNonSupprimable).
	const retire = documentRetire(tx)
	const figee =
		!!enBase(tx).date || !!tx.quittance || !!tx.facture || !!retire
	if (enregistree(tx) && niveau === EMPLOYE && figee) {
		c.moyen = c.payeur = c.montant = false
		c.pourquoi = enBase(tx).date
			? "Encaissée : montant, moyen et payeur modifiables par le boss ou l'admin"
			: tx.facture
				? "Facturée : montant, moyen et payeur modifiables par le boss ou l'admin"
				: tx.quittance
					? "Quittancée : montant, moyen et payeur modifiables par le boss ou l'admin"
					: `Dont la ${retire} a été retirée : montant, moyen et payeur modifiables par le boss ou l'admin`
	}
	if (tx.couverture) {
		c.moyen = c.payeur = c.montant = false
		c.pourquoi =
			"Payeur, moyen et montant suivent la couverture d'assistance"
	}
	if (tx.rapprochee && !bossAdmin) {
		c.montant = false
		c.pourquoi =
			"Rapprochée : date et montant figés — défaire d'abord l'appariement dans Rapprochement"
	}
	return c
}

// Revue interne 19.10 (R10) : document retiré d'une ligne qui garde son
// moyen (hors perte) → elle reste protégée (location/jours.cause_forte).
const documentRetire = (tx) => {
	const retraits = (tx.extra_data || {}).document_retire || []
	if (!retraits.length) return null
	const moyen =
		tx.enregistre && "moyenPaie" in tx.enregistre
			? tx.enregistre.moyenPaie
			: tx.moyenPaie
	if (!moyen || tx.payeParSelect === PAYE_PAR_PERTE) return null
	return retraits[retraits.length - 1].doc || "document"
}

// Cause qui interdit de supprimer une ligne enregistrée (miroir de
// utilitaires/utils.cause_non_supprimable), ou null. Le boss et l'admin
// suppriment tout (Jordan 30.10 ; la fiche close est gérée à part) ; le
// responsable, toute ligne sans date (WinBIZ et rapprochée en filet).
export const causeNonSupprimable = (tx, { niveau }) => {
	if (!enregistree(tx) || niveau === BOSS || niveau === ADMIN) return null
	if (niveau === RESPONSABLE)
		return enBase(tx).date
			? "Encaissée"
			: tx.winbiz
				? "Envoyée à WinBIZ"
				: tx.rapprochee
					? "Rapprochée"
					: null
	if (enBase(tx).date) return "Encaissée"
	if (tx.winbiz) return "Envoyée à WinBIZ"
	if (tx.facture) return "Facturée"
	if (tx.quittance) return "Quittancée"
	const retire = documentRetire(tx)
	if (retire) return `Dont la ${retire} a été retirée`
	if (tx.rapprochee) return "Rapprochée"
	return null
}

// Ligne qui ne part qu'avec une demande explicite (`transactions_supprimees`)
// — le serveur refuse sinon (« rechargez la fiche »).
export const estProtegee = (tx) =>
	enregistree(tx) &&
	(!!enBase(tx).date ||
		!!tx.winbiz ||
		!!tx.facture ||
		!!tx.quittance ||
		!!documentRetire(tx) ||
		!!tx.rapprochee)

const montant = (v) => (parseFloat(v || 0) || 0).toFixed(2)

// Conséquences à confirmer avant le save (garde-fous non bloquants du boss,
// documents archivés pour tous) : lignes modifiées et lignes supprimées.
export const consequencesTransactions = ({
	transactions,
	supprimees = [],
	close = false,
}) => {
	const messages = []
	;(transactions || []).forEach((tx, i) => {
		if (!enregistree(tx) || !tx.enregistre) {
			if (close && !enregistree(tx))
				messages.push(
					`Transaction #${i + 1} ajoutée sur la fiche close (note de suivi).`,
				)
			return
		}
		const b = tx.enregistre
		const doc =
			Math.round(parseFloat(tx.total || 0) * 100) !==
				Math.round(parseFloat(b.total || 0) * 100) ||
			(tx.moyenPaie || 0) !== (b.moyenPaie || 0) ||
			tx.payeParSelect !== b.payeParSelect
		const date = (tx.date || null) !== (b.date || null)
		const caissier = (tx.caissier || 0) !== (b.caissier || 0)
		if (!doc && !date && !caissier) return
		const n = `Transaction #${i + 1}`
		if (doc && (tx.quittance || tx.facture))
			messages.push(
				`${n} : ${
					tx.quittance && tx.facture
						? "la quittance et la facture ne correspondent"
						: tx.quittance
							? "la quittance ne correspond"
							: "la facture ne correspond"
				} plus — archivée(s), à régénérer.`,
			)
		if (tx.winbiz)
			messages.push(
				`${n} : envoyée à WinBIZ — corriger aussi dans WinBIZ (note de suivi).`,
			)
		if (tx.rapprochee && (doc || date))
			messages.push(
				`${n} : rapprochée d'une ligne bancaire — vérifier le rapprochement (note de suivi).`,
			)
		if (close)
			messages.push(`${n} : modifiée sur la fiche close (note de suivi).`)
	})
	supprimees.forEach((tx) => {
		if (!estProtegee(tx) && !close) return
		const winbiz = (tx.extra_data || {}).winbiz
		const numero = winbiz && winbiz.numero ? winbiz.numero : "?"
		messages.push(
			`Transaction de ${montant(tx.total)} supprimée${
				close ? " sur la fiche close" : ""
			} (note de suivi)${
				tx.winbiz
					? ` — facture WinBIZ n° ${numero} à supprimer dans WinBIZ`
					: ""
			}${
				tx.rapprochee
					? " — la ligne bancaire retourne au rapprochement"
					: ""
			}${tx.quittance || tx.facture ? " — document archivé" : ""}.`,
		)
	})
	return messages
}
